/**
 * Dashboards — custom wallboards: tabs (rename/clone/delete), fullscreen, widget chrome, edit modal.
 * All tab grids stay mounted (CSS-hidden when inactive) so widgets keep state and skip reload on rotate.
 * No page title; content scales with tile; access = Supervisor+ or can_access_custom_dashboards.
 */
import 'react-grid-layout/css/styles.css';
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import GridLayout, { type Layout, type LayoutItem } from 'react-grid-layout';
import { Plus, LayoutGrid, Maximize2, Minimize2, Pencil, X, RotateCw, Pause, Play, Copy } from 'lucide-react';
import WidgetAbandonedInQueue from '../components/widgets/WidgetAbandonedInQueue';
import WidgetAbandonedCallsTable from '../components/widgets/WidgetAbandonedCallsTable';
import WidgetAgentPresence from '../components/widgets/WidgetAgentPresence';
import WidgetAgentStatusTime from '../components/widgets/WidgetAgentStatusTime';
import WidgetCallMetrics from '../components/widgets/WidgetCallMetrics';
import WidgetCallWrapPie from '../components/widgets/WidgetCallWrapPie';
import WidgetCallHourlyChart from '../components/widgets/WidgetCallHourlyChart';
import WidgetOutboundContactAttempts from '../components/widgets/WidgetOutboundContactAttempts';
import WidgetOutboundCallbackRate from '../components/widgets/WidgetOutboundCallbackRate';
import type { WidgetFilterConfig } from '../lib/widgetScope';
import DashboardWidgetChrome from '../components/widgets/DashboardWidgetChrome';
import WidgetEditModal, { type WidgetConfig } from '../components/widgets/WidgetEditModal';

interface DashboardWidget {
  id: string;
  type: string;
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: Record<string, unknown>;
}

interface DashboardTab {
  id: string;
  name: string;
  sort_order: number;
  rotation_interval: number;
  widgets: DashboardWidget[];
}

const WIDGET_TYPES: { type: string; label: string }[] = [
  { type: 'abandoned-in-queue', label: 'Abandoned in Queue' },
  { type: 'abandoned-calls-table', label: 'Abandoned Calls (table)' },
  { type: 'call-metrics', label: 'Call metrics' },
  { type: 'call-wrap-pie', label: 'Wrap codes (pie)' },
  { type: 'call-hourly-chart', label: 'Calls by hour (stacked bar)' },
  { type: 'agent-presence', label: 'Agent Presence' },
  { type: 'agent-status-time', label: 'Agent status time' },
  { type: 'outbound-contact-attempts', label: 'Outbound contact attempts' },
  { type: 'outbound-callback-rate', label: 'Returned calls rate' },
  { type: 'placeholder', label: 'Placeholder' },
];

export default function Dashboards() {
  const { getToken } = useAuth();
  const [tabs, setTabs] = useState<DashboardTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingWidget, setAddingWidget] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotationIntervalSec, setRotationIntervalSec] = useState(10);
  const [rotationPaused, setRotationPaused] = useState(false);
  const [rotationModalOpen, setRotationModalOpen] = useState(false);
  const rotationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [tabRenamingId, setTabRenamingId] = useState<string | null>(null);
  const [tabRenameValue, setTabRenameValue] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(1200);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setGridWidth(el.offsetWidth));
    ro.observe(el);
    setGridWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, [activeTabId, tabs.length]);

  /** Nudge react-grid-layout / Recharts after showing a previously hidden tab. */
  useEffect(() => {
    if (!activeTabId) return;
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeTabId]);

  const fetchTabs = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/tabs`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized' : 'Failed to load dashboards');
      const data = await res.json();
      setTabs(data.tabs ?? []);
      if (data.tabs?.length && !activeTabId) setActiveTabId(data.tabs[0].id);
      if (activeTabId && !(data.tabs ?? []).some((t: { id: string }) => t.id === activeTabId)) {
        setActiveTabId(data.tabs?.[0]?.id ?? null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken, activeTabId]);

  useEffect(() => {
    fetchTabs();
  }, [fetchTabs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const user = data?.user;
        const role = user?.role ?? 'agent';
        const canAccess = !!(user?.can_access_custom_dashboards ?? 0);
        if (cancelled) return;
        setAccessDenied(role === 'agent' && !canAccess);
      } catch {
        if (!cancelled) setAccessDenied(true);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const layoutFromWidgets = (widgets: DashboardWidget[]): Layout =>
    widgets.map((w) => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h })) as Layout;

  const handleLayoutChangeForTab = useCallback(
    (tabId: string) => async (newLayout: Layout) => {
      if (!getToken) return;
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;
      const token = await getToken();
      if (!token) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const layoutItems = Array.from(newLayout) as LayoutItem[];
      for (const item of layoutItems) {
        const w = tab.widgets.find((x) => x.id === item.i);
        if (!w || (w.x === item.x && w.y === item.y && w.w === item.w && w.h === item.h)) continue;
        try {
          await fetch(`${base}/api/dashboards/widgets/${item.i}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ x: item.x, y: item.y, w: item.w, h: item.h }),
          });
        } catch {
          // ignore per-widget errors
        }
      }
      setTabs((prev) =>
        prev.map((t) => {
          if (t.id !== tabId) return t;
          return {
            ...t,
            widgets: t.widgets.map((w) => {
              const l = layoutItems.find((x) => x.i === w.id);
              if (!l) return w;
              return { ...w, x: l.x, y: l.y, w: l.w, h: l.h };
            }),
          };
        })
      );
      setLastSaved(new Date());
    },
    [tabs, getToken]
  );

  const addTab = async () => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/tabs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: `Dashboard ${tabs.length + 1}`, sort_order: tabs.length }),
      });
      if (!res.ok) throw new Error('Failed to create tab');
      const data = await res.json();
      setTabs((prev) => [...prev, { id: data.id, name: data.name, sort_order: data.sort_order, rotation_interval: 10, widgets: data.widgets ?? [] }]);
      setActiveTabId(data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add dashboard');
    }
  };

  const cloneTab = async (tabId: string) => {
    const source = tabs.find((t) => t.id === tabId);
    if (!source) return;
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/tabs/${tabId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sort_order: tabs.length }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || 'Failed to clone dashboard');
      }
      const data = await res.json();
      const cloned: DashboardTab = {
        id: data.id,
        name: data.name,
        sort_order: data.sort_order ?? tabs.length,
        rotation_interval: data.rotation_interval ?? source.rotation_interval ?? 10,
        widgets: data.widgets ?? [],
      };
      setTabs((prev) => [...prev, cloned]);
      setActiveTabId(cloned.id);
      setTabRenamingId(cloned.id);
      setTabRenameValue(cloned.name);
      setLastSaved(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clone dashboard');
    }
  };

  const addWidget = async (type: string, title: string) => {
    if (!activeTabId) return;
    const token = await getToken();
    if (!token) return;
    setAddingWidget(false);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const widgets = activeTab?.widgets ?? [];
    const maxY = widgets.length ? Math.max(...widgets.map((w) => w.y + w.h), 0) : 0;
    const defaultIcons: Record<string, string> = {
      'abandoned-in-queue': 'PhoneOff',
      'abandoned-calls-table': 'PhoneOff',
      'call-metrics': 'Phone',
      'call-wrap-pie': 'TrendingUp',
      'call-hourly-chart': 'BarChart3',
      'agent-presence': 'Users',
      'agent-status-time': 'BarChart3',
      'outbound-contact-attempts': 'BarChart3',
      'outbound-callback-rate': 'PhoneOutgoing',
    };
    const callTypes = new Set(['abandoned-in-queue', 'abandoned-calls-table', 'call-metrics', 'call-wrap-pie', 'call-hourly-chart']);
    const statusTimeTypes = new Set(['agent-status-time']);
    const analyticsTypes = new Set(['outbound-contact-attempts', 'outbound-callback-rate']);
    const config: Record<string, unknown> = defaultIcons[type]
      ? {
          icon: defaultIcons[type],
          ...(callTypes.has(type) || statusTimeTypes.has(type) || analyticsTypes.has(type)
            ? { date_range: 'today' }
            : {}),
          ...(statusTimeTypes.has(type) ? { time_view: 'pie', filter_type: 'agent' } : {}),
        }
      : {};
    const wideTypes = new Set([
      'abandoned-in-queue',
      'call-metrics',
      'call-wrap-pie',
      'call-hourly-chart',
      'abandoned-calls-table',
      'agent-status-time',
      'outbound-contact-attempts',
      'outbound-callback-rate',
    ]);
    const w = type === 'call-hourly-chart' ? 6 : wideTypes.has(type) ? 4 : 2;
    const h = wideTypes.has(type) ? 3 : 2;
    try {
      const res = await fetch(`${base}/api/dashboards/widgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tab_id: activeTabId,
          type,
          title,
          x: 0,
          y: maxY,
          w,
          h,
          config,
        }),
      });
      if (!res.ok) throw new Error('Failed to add widget');
      const data = await res.json();
      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === activeTabId
            ? { ...tab, widgets: [...tab.widgets, { id: data.id, type: data.type, title: data.title, x: data.x, y: data.y, w: data.w, h: data.h, config: data.config ?? config }] }
            : tab
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add widget');
    }
  };

  const deleteWidget = async (widgetId: string) => {
    const token = await getToken();
    if (!token || !activeTabId) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/widgets/${widgetId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete');
      setTabs((prev) =>
        prev.map((tab) => (tab.id === activeTabId ? { ...tab, widgets: tab.widgets.filter((w) => w.id !== widgetId) } : tab))
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete widget');
    }
  };

  const saveWidgetEdit = async (widgetId: string, title: string, config: WidgetConfig) => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/widgets/${widgetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, config }),
      });
      if (!res.ok) throw new Error('Failed to update widget');
      setTabs((prev) =>
        prev.map((tab) => ({
          ...tab,
          widgets: tab.widgets.map((w) =>
            w.id === widgetId ? { ...w, title, config: { ...w.config, ...config } } : w
          ),
        }))
      );
      setEditingWidgetId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save widget');
    }
  };

  const renameTab = async (tabId: string, name: string) => {
    if (!name.trim()) { setTabRenamingId(null); return; }
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/tabs/${tabId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) throw new Error('Failed to rename');
      setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, name: name.trim() } : t)));
      setTabRenamingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to rename dashboard');
    }
  };

  const deleteTab = async (tabId: string) => {
    if (tabs.length <= 1) return;
    if (!window.confirm('Delete this dashboard? Its widgets will be removed.')) return;
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/dashboards/tabs/${tabId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete');
      setTabs((prev) => prev.filter((t) => t.id !== tabId));
      if (activeTabId === tabId) setActiveTabId(tabs.find((t) => t.id !== tabId)?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete dashboard');
    }
  };

  const renderWidgetContent = (w: DashboardWidget) => {
    const cfg = w.config as WidgetFilterConfig;
    if (w.type === 'abandoned-in-queue') {
      return <WidgetAbandonedInQueue config={cfg} />;
    }
    if (w.type === 'abandoned-calls-table') {
      return <WidgetAbandonedCallsTable config={cfg} />;
    }
    if (w.type === 'call-metrics') {
      return <WidgetCallMetrics config={cfg} />;
    }
    if (w.type === 'call-wrap-pie') {
      return <WidgetCallWrapPie config={cfg} />;
    }
    if (w.type === 'call-hourly-chart') {
      return <WidgetCallHourlyChart config={cfg} />;
    }
    if (w.type === 'agent-presence') {
      return <WidgetAgentPresence config={cfg} />;
    }
    if (w.type === 'agent-status-time') {
      return <WidgetAgentStatusTime config={cfg} />;
    }
    if (w.type === 'outbound-contact-attempts') {
      return <WidgetOutboundContactAttempts config={cfg} />;
    }
    if (w.type === 'outbound-callback-rate') {
      return <WidgetOutboundCallbackRate config={cfg} />;
    }
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 'clamp(0.75rem, 2cqi, 1rem)' }}>
        {w.title}
      </div>
    );
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) document.exitFullscreen();
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const enterFullscreen = async () => {
    if (!fullscreenRef.current) return;
    try {
      await fullscreenRef.current.requestFullscreen();
      setIsFullscreen(true);
    } catch {
      setIsFullscreen(true);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {}
    setIsFullscreen(false);
  };

  useEffect(() => {
    if (!isFullscreen || tabs.length <= 1 || rotationIntervalSec <= 0 || rotationPaused) {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
      return;
    }
    rotationTimerRef.current = setInterval(() => {
      setActiveTabId((current) => {
        const idx = tabs.findIndex((t) => t.id === current);
        const next = (idx + 1) % tabs.length;
        return tabs[next]?.id ?? current;
      });
    }, rotationIntervalSec * 1000);
    return () => {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
    };
  }, [isFullscreen, tabs.length, rotationIntervalSec, rotationPaused]);

  if (loading) {
    return (
      <div style={{ padding: '1rem' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading dashboards…</p>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <p>You don&apos;t have access to custom dashboards. Ask your administrator to enable it for your account.</p>
      </div>
    );
  }

  const editingWidget = activeTab?.widgets.find((w) => w.id === editingWidgetId);

  return (
    <div style={{ padding: '1rem', minHeight: '100%', position: 'relative' }}>
      <div ref={fullscreenRef} style={{ minHeight: '100%', background: isFullscreen ? 'var(--bg)' : undefined }}>
        {/* Single row: tabs + New dashboard on left; Saved / Add widget / Fullscreen / Rotation on right */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
            {tabs.map((tab) => (
            <div
              key={tab.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: activeTabId === tab.id ? 'rgba(59,130,246,0.2)' : 'var(--widget-bg)',
                border: '1px solid var(--header-border)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {tabRenamingId === tab.id ? (
                <input
                  type="text"
                  value={tabRenameValue}
                  onChange={(e) => setTabRenameValue(e.target.value)}
                  onBlur={() => renameTab(tab.id, tabRenameValue)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') renameTab(tab.id, tabRenameValue);
                    if (e.key === 'Escape') setTabRenamingId(null);
                  }}
                  autoFocus
                  style={{
                    width: 120,
                    padding: '0.4rem 0.5rem',
                    border: 'none',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: '0.875rem',
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveTabId(tab.id)}
                  onDoubleClick={() => { setTabRenamingId(tab.id); setTabRenameValue(tab.name); }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    background: 'none',
                    color: activeTabId === tab.id ? 'var(--accent)' : 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  {tab.name}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setTabRenamingId(tab.id); setTabRenameValue(tab.name); }}
                style={{ padding: 4, border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                aria-label="Rename"
              >
                <Pencil size={12} />
              </button>
              {!isFullscreen && (
                <button
                  type="button"
                  onClick={() => void cloneTab(tab.id)}
                  style={{ padding: 4, border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                  aria-label="Clone dashboard"
                  title="Clone dashboard (layout and widgets)"
                >
                  <Copy size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteTab(tab.id)}
                disabled={tabs.length <= 1}
                style={{ padding: 4, border: 'none', background: 'none', color: tabs.length <= 1 ? 'var(--text-muted)' : 'var(--text-muted)', cursor: tabs.length <= 1 ? 'not-allowed' : 'pointer', opacity: tabs.length <= 1 ? 0.5 : 1 }}
                aria-label="Delete dashboard"
              >
                <X size={12} />
              </button>
            </div>
          ))}
            {!isFullscreen && (
              <button
                type="button"
                onClick={addTab}
                style={{
                  padding: '0.4rem 0.5rem',
                  border: '1px dashed var(--header-border)',
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
                title="New dashboard"
              >
                <Plus size={16} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!isFullscreen && lastSaved && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Saved {lastSaved.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {!isFullscreen && activeTabId && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setAddingWidget((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.4rem 0.6rem',
                    borderRadius: 6,
                    border: '1px solid var(--header-border)',
                    background: 'var(--widget-bg)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                  }}
                >
                  <LayoutGrid size={16} />
                  Add widget
                </button>
                {addingWidget && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 4,
                      background: 'var(--widget-bg)',
                      border: '1px solid var(--header-border)',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      zIndex: 10,
                      minWidth: 200,
                    }}
                  >
                    {WIDGET_TYPES.map(({ type, label }) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => addWidget(type, label)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.5rem 1rem',
                          textAlign: 'left',
                          border: 'none',
                          background: 'none',
                          color: 'var(--text)',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!isFullscreen ? (
              <button
                type="button"
                onClick={enterFullscreen}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.4rem 0.6rem',
                  borderRadius: 6,
                  border: '1px solid var(--header-border)',
                  background: 'var(--widget-bg)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                }}
              >
                <Maximize2 size={16} />
                Fullscreen
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setRotationModalOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.4rem 0.6rem',
                    borderRadius: 6,
                    border: '1px solid var(--header-border)',
                    background: 'var(--widget-bg)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                  }}
                >
                  <RotateCw size={16} />
                  Rotate
                </button>
                {tabs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setRotationPaused((p) => !p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.4rem 0.6rem',
                      borderRadius: 6,
                      border: '1px solid var(--header-border)',
                      background: rotationPaused ? 'var(--accent)' : 'var(--widget-bg)',
                      color: 'var(--text)',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                    }}
                    title={rotationPaused ? 'Resume rotation' : 'Pause rotation'}
                  >
                    {rotationPaused ? <Play size={16} /> : <Pause size={16} />}
                    {rotationPaused ? 'Resume' : 'Pause'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={exitFullscreen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.4rem 0.6rem',
                    borderRadius: 6,
                    border: '1px solid var(--header-border)',
                    background: 'var(--widget-bg)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                  }}
                >
                  <Minimize2 size={16} />
                  Exit fullscreen
                </button>
              </>
            )}
          </div>
        </div>

      {error && <p style={{ color: 'var(--error)', marginBottom: '1rem' }}>{error}</p>}

      {tabs.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <p style={{ marginBottom: '1rem' }}>No dashboards yet. Create one to add widgets like Abandoned in Queue.</p>
          <button
            type="button"
            onClick={addTab}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: 6,
              border: '1px solid var(--accent)',
              background: 'var(--accent)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Create dashboard
          </button>
        </div>
      ) : tabs.length > 0 ? (
        <>
          <div ref={gridRef} style={{ width: '100%', minHeight: isFullscreen ? '100vh' : 400, position: 'relative' }}>
            {tabs.map((tab) => {
              const isActive = tab.id === activeTabId;
              return (
                <div
                  key={tab.id}
                  role="tabpanel"
                  aria-hidden={!isActive}
                  style={{
                    display: isActive ? 'block' : 'none',
                    width: '100%',
                    minHeight: isFullscreen ? '100vh' : 400,
                  }}
                >
                  <GridLayout
                    className="layout"
                    layout={layoutFromWidgets(tab.widgets)}
                    onLayoutChange={(layout: Layout) => {
                      if (isActive) void handleLayoutChangeForTab(tab.id)(layout);
                    }}
                    width={gridWidth}
                    gridConfig={{ cols: 12, rowHeight: 120 }}
                    dragConfig={{ enabled: isActive && !isFullscreen, handle: '.widget-drag-handle' }}
                  >
                    {tab.widgets.map((w) => (
                      <div
                        key={w.id}
                        className="widget-drag-handle"
                        style={{ height: '100%', cursor: isFullscreen ? 'default' : 'grab' }}
                      >
                        <DashboardWidgetChrome
                          title={w.title}
                          iconName={(w.config?.icon as string) ?? undefined}
                          onEdit={() => setEditingWidgetId(w.id)}
                          onRemove={() => deleteWidget(w.id)}
                          hideActions={isFullscreen}
                        >
                          {renderWidgetContent(w)}
                        </DashboardWidgetChrome>
                      </div>
                    ))}
                  </GridLayout>
                </div>
              );
            })}
          </div>
          {isFullscreen && (
            <button
              type="button"
              onClick={exitFullscreen}
              style={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: 8,
                border: '1px solid var(--header-border)',
                background: 'var(--widget-bg)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              <Minimize2 size={18} />
              Exit fullscreen
            </button>
          )}
        </>
      ) : null}

      {rotationModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setRotationModalOpen(false)}
        >
          <div
            style={{
              background: 'var(--widget-bg)',
              border: '1px solid var(--header-border)',
              borderRadius: 12,
              padding: '1.25rem',
              minWidth: 280,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Rotation Settings</h3>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Interval (seconds)
            </label>
            <input
              type="number"
              min={5}
              max={120}
              value={rotationIntervalSec}
              onChange={(e) => setRotationIntervalSec(Math.max(5, Math.min(120, Number(e.target.value) || 5)))}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                marginBottom: '1rem',
                borderRadius: 6,
                border: '1px solid var(--header-border)',
                background: 'var(--bg)',
                color: 'var(--text)',
                fontSize: '0.875rem',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setRotationModalOpen(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--accent)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {editingWidget && (
        <WidgetEditModal
          widgetId={editingWidget.id}
          widgetType={editingWidget.type}
          title={editingWidget.title}
          config={(editingWidget.config || {}) as WidgetConfig}
          onSave={(title, config) => saveWidgetEdit(editingWidget.id, title, config)}
          onClose={() => setEditingWidgetId(null)}
        />
      )}
      </div>
    </div>
  );
}
